/**
 * Readies a photo for the bulletin PDF.
 *
 * The PDF embeds whatever bytes it is given, so a phone original goes in at full size: a 4032x3024
 * iPhone JPEG is 5 MB, and a PDF with six of them is too large to email — which is how Peel sends
 * bulletins out. A featured photo prints at most 260pt tall, so 2000px on the long edge is still
 * well over 300 dpi at that size.
 *
 * It is also the one place a format react-pdf cannot embed (anything but JPEG and PNG — HEIC, WebP,
 * AVIF) becomes one it can: the browser decodes it, the canvas re-encodes it as JPEG.
 */

/** Longest edge, in pixels, a photo keeps in the PDF. */
export const PDF_IMAGE_MAX_EDGE = 2000;

export const PDF_IMAGE_QUALITY = 0.85;

/** The formats react-pdf embeds as they are. */
const EMBEDDABLE_TYPES = ["image/jpeg", "image/jpg", "image/png"];

/** Scaled to fit `maxEdge`, never up. */
export const scaledSize = (
  width: number,
  height: number,
  maxEdge: number = PDF_IMAGE_MAX_EDGE,
): { width: number; height: number } => {
  const scale = Math.min(1, maxEdge / Math.max(width, height));
  return {
    width: Math.round(width * scale),
    height: Math.round(height * scale),
  };
};

/**
 * Returns a JPEG no larger than `PDF_IMAGE_MAX_EDGE`, or the original blob when it already fits and
 * react-pdf can embed it.
 *
 * Never throws: any failure — a format the browser cannot decode either, no canvas, an encoder
 * that returns nothing — hands back the original, which is no worse than before this existed.
 */
export const prepareImageForPDF = async (blob: Blob): Promise<Blob> => {
  try {
    // Orientation applied here, so the re-encoded JPEG — which carries no EXIF — is upright.
    const bitmap = await createImageBitmap(blob, {
      imageOrientation: "from-image",
    });

    try {
      const fits = Math.max(bitmap.width, bitmap.height) <= PDF_IMAGE_MAX_EDGE;
      if (fits && EMBEDDABLE_TYPES.includes(blob.type)) return blob;

      const { width, height } = scaledSize(bitmap.width, bitmap.height);
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext("2d");
      if (!context) return blob;

      // JPEG has no alpha: without a fill, a transparent PNG's background prints black.
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, width, height);
      context.drawImage(bitmap, 0, 0, width, height);

      const encoded = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, "image/jpeg", PDF_IMAGE_QUALITY),
      );
      return encoded ?? blob;
    } finally {
      bitmap.close();
    }
  } catch (error) {
    console.error("Could not prepare image for PDF; using the original", error);
    return blob;
  }
};
